import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { X } from 'lucide-react';
import { updateBackofficeProjectParticipantPrivacy } from '../api';
import type { BackofficeProjectDetail } from '../types';

export interface EditProjectParticipantPrivacyModalProps {
  project: BackofficeProjectDetail;
  onClose: () => void;
  onUpdated: (project: BackofficeProjectDetail) => void;
}

const RECORDING_PRIVACY_OPTIONS = [
  {
    value: 'transparent',
    label: 'Transparent',
    description: 'Original recordings are available for playback.',
  },
  {
    value: 'obfuscated',
    label: 'Obfuscated',
    description: 'Recordings are distorted for playback.',
  },
  {
    value: 'hidden',
    label: 'Hidden',
    description: 'Recordings are not available for playback.',
  },
] as const;

export function EditProjectParticipantPrivacyModal({
  project,
  onClose,
  onUpdated,
}: EditProjectParticipantPrivacyModalProps) {
  const [participantRecordingPrivacyMode, setParticipantRecordingPrivacyMode] = useState<
    BackofficeProjectDetail['participantRecordingPrivacyMode']
  >(project.participantRecordingPrivacyMode);
  const [collectParticipantEmail, setCollectParticipantEmail] = useState(
    project.collectParticipantEmail
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    closeButtonRef.current?.focus();
  }, []);

  const dirty = useMemo(
    () =>
      participantRecordingPrivacyMode !== project.participantRecordingPrivacyMode ||
      collectParticipantEmail !== project.collectParticipantEmail,
    [
      collectParticipantEmail,
      participantRecordingPrivacyMode,
      project.collectParticipantEmail,
      project.participantRecordingPrivacyMode,
    ]
  );

  const selectedPrivacyOption =
    RECORDING_PRIVACY_OPTIONS.find((option) => option.value === participantRecordingPrivacyMode) ??
    RECORDING_PRIVACY_OPTIONS[0];

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!dirty || saving) return;

    setSaving(true);
    setError(null);

    try {
      const response = await updateBackofficeProjectParticipantPrivacy(project.id, {
        participantRecordingPrivacyMode,
        collectParticipantEmail,
      });
      onUpdated(response.project);
      onClose();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Failed to update participant privacy');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-neutral-950/40 p-4"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !saving) {
          onClose();
        }
      }}
    >
      <form
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-project-participant-privacy-title"
        className="max-h-[90vh] w-full max-w-2xl overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-xl"
        onSubmit={handleSubmit}
      >
        <div className="flex items-start justify-between gap-4 border-b border-neutral-200 px-5 py-4">
          <div>
            <h2 id="edit-project-participant-privacy-title" className="text-title font-semibold">
              Edit participant privacy
            </h2>
            <p className="mt-1 text-small text-neutral-500">
              Change participant recording playback and finished-screen email collection for{' '}
              {project.title}.
            </p>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            className="focus-ring inline-flex size-9 items-center justify-center rounded-lg border border-neutral-200 bg-white text-neutral-600 hover:bg-neutral-50"
            onClick={onClose}
            disabled={saving}
            aria-label="Close participant privacy editor"
          >
            <X className="size-4" aria-hidden="true" />
          </button>
        </div>

        <div className="max-h-[60vh] overflow-y-auto px-5 py-4">
          {error ? (
            <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-small text-red-700">
              {error}
            </div>
          ) : null}

          <div className="space-y-4">
            <section className="rounded-xl border border-neutral-200 p-4">
              <label
                htmlFor="participant-recording-privacy-mode"
                className="block text-sm font-semibold text-neutral-900"
              >
                Interview recording privacy
              </label>
              <p className="mt-1 text-sm text-neutral-500">
                Choose how participant recordings should be available in playback.
              </p>
              <select
                id="participant-recording-privacy-mode"
                className="focus-ring mt-4 block w-full rounded-lg border border-neutral-200 bg-white px-3 py-2 text-sm text-neutral-700"
                value={participantRecordingPrivacyMode}
                onChange={(event) => {
                  setParticipantRecordingPrivacyMode(
                    event.target.value as BackofficeProjectDetail['participantRecordingPrivacyMode']
                  );
                  setError(null);
                }}
                disabled={saving}
              >
                {RECORDING_PRIVACY_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <p className="mt-3 text-sm text-neutral-500">{selectedPrivacyOption.description}</p>
            </section>

            <section className="rounded-xl border border-neutral-200 p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-neutral-900">
                    Collect participant email
                  </p>
                  <p className="mt-1 text-sm text-neutral-500">
                    Let participants leave an email address on the finished screen.
                  </p>
                  <p className="mt-3 text-sm text-neutral-500">
                    {collectParticipantEmail
                      ? 'Participants can leave their email address after the completed interview.'
                      : 'Participants will see the default thank-you page after the interview.'}
                  </p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={collectParticipantEmail}
                  aria-label="Collect participant email"
                  className={[
                    'focus-ring relative inline-flex h-6 w-11 shrink-0 rounded-full border-2 border-transparent transition-colors disabled:cursor-not-allowed disabled:opacity-50',
                    collectParticipantEmail ? 'bg-backoffice-500' : 'bg-neutral-300',
                  ].join(' ')}
                  onClick={() => {
                    setCollectParticipantEmail((current) => !current);
                    setError(null);
                  }}
                  disabled={saving}
                >
                  <span
                    className={[
                      'inline-block size-5 rounded-full bg-white shadow-sm transition-transform',
                      collectParticipantEmail ? 'translate-x-5' : 'translate-x-0',
                    ].join(' ')}
                    aria-hidden="true"
                  />
                </button>
              </div>
            </section>
          </div>
        </div>

        <div className="flex flex-wrap justify-end gap-2 border-t border-neutral-200 px-5 py-4">
          <button
            type="submit"
            className="focus-ring inline-flex items-center rounded-lg bg-backoffice-500 px-4 py-2 text-small font-semibold text-white hover:bg-backoffice-600 disabled:cursor-not-allowed disabled:opacity-50"
            disabled={!dirty || saving}
          >
            {saving ? 'Saving' : 'Save changes'}
          </button>
        </div>
      </form>
    </div>
  );
}
